import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import CustomError from "../../../../utils/customError";
import PhoneInput from "../../../../utils/PhoneInput";
import { userAdd } from "../../services/user.service";
import { useLoadScript } from "@react-google-maps/api";
import {
  SWAL_SETTINGS,
  GENDER,
  TEXTAREA_MAX_LENGTH,
} from "../../../../utils/Constants";
import {
  handleServerValidations,
  globalLoader,
  convertTimezoneToOffset,
} from "../../../../utils/commonfunction";
import {
  EMAIL_VALIDATION,
  PHONE_VALIDATION,
} from "../../../../utils/commonValidations";

const LIBRARIES = ["places"];

const UserAdd = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [authMethod, setAuthMethod] = useState("email");
  const [submitted, setSubmitted] = useState(false);
  const inputRef = useRef(null);

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    { title: t("User"), url: "/admin/user-management/user/list/1" },
    { title: t("Add"), url: "" },
  ];

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries: LIBRARIES,
  });

  // ✅ Dynamic Validation Schema
  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().max(100, t("max_length_error")).required(t("Name is required")),
    gender: Yup.string().required(t("Gender is required")),
    date_of_birth: Yup.string().required(t("Date of Birth is required")),
    time_of_birth: Yup.string().required(t("Time of Birth is required")),
    place_of_birth: Yup.string()
      .max(TEXTAREA_MAX_LENGTH, t("validation_max_input_characters"))
      .required(t("Place of Birth is required")),
    ...(authMethod === "email"
      ? {
          email: EMAIL_VALIDATION.required(t("label_email_required_error")),
        }
      : {
          phone_number: PHONE_VALIDATION.required(t("label_phone_number_required_error")),
        }),
    profile_image: Yup.mixed()
      .required(t("image_required_error"))
      .test("fileOrUrl", "supported_file_error", (value) => {
        if (!value) return true;
        if (typeof value === "string")
          return /^(http|https):\/\/[^\s$.?#].[^\s]*$/gm.test(value);
        return ["image/jpg", "image/jpeg", "image/png", "image/webp"].includes(
          value.type
        );
      })
      .test("fileSize", "image_max_size_error", (value) => {
        if (!value || typeof value === "string") return true;
        return value.size <= 1.5 * 1024 * 1024;
      }),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone_number: "",
      gender: "",
      date_of_birth: "",
      time_of_birth: "",
      place_of_birth: "",
      latitude: "",
      longitude: "",
      timezone: "",
      profile_image: "",
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      globalLoader(true);
      setSubmitted(true);

      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("gender", values.gender);
      formData.append("date_of_birth", values.date_of_birth);
      formData.append("time_of_birth", values.time_of_birth);
      formData.append("place_of_birth", values.place_of_birth);
      formData.append("latitude", values.latitude);
      formData.append("longitude", values.longitude);
      formData.append("timezone", values.timezone);
      formData.append("profile_image", values.profile_image);
      formData.append("login_with", authMethod);

      if (authMethod === "email") formData.append("email", values.email);
      else formData.append("phone_number", values.phone_number);

      userAdd(formData)
        .then((response) => {
          globalLoader(false);
          setSubmitted(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            resetForm();
            navigate("/admin/user-management/user/list/1");
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          globalLoader(false);
          setSubmitted(false);
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
    },
  });

  // ✅ Google Maps Autocomplete for Place of Birth
  useEffect(() => {
    if (isLoaded && window.google && inputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        { types: ["geocode"] }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place && place.geometry) {
          const latitude = place.geometry.location.lat();
          const longitude = place.geometry.location.lng();

          formik.setFieldValue("place_of_birth", place.formatted_address);
          formik.setFieldValue("latitude", latitude);
          formik.setFieldValue("longitude", longitude);

          const timestamp = new Date().getTime() / 1000;
          const timezoneUrl = `https://maps.googleapis.com/maps/api/timezone/json?location=${latitude},${longitude}&timestamp=${timestamp}&key=${process.env.REACT_APP_GOOGLE_MAP_API_KEY}`;

          fetch(timezoneUrl)
            .then((response) => response.json())
            .then((data) => {
              if (data.status === "OK") {
                const timezoneId = data.timeZoneId;
                formik.setFieldValue(
                  "timezone",
                  convertTimezoneToOffset(timezoneId)
                );
              } else {
                console.error("Error fetching timezone:", data);
              }
            })
            .catch((error) => console.error("Timezone fetch error:", error));
        }
      });
    }
  }, [isLoaded]);

  if (loadError) return <div>Failed to load Google Maps API</div>;
  console.log("Formik Values:", formik.values);
  console.log("Formik Errors:", formik.errors);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <h6 className="main-content-label mb-3">{t("Add User")}</h6>
                <div className="row row-sm">
                  
                  {/* Login Method Toggle */}
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">
                      Registration Method:<span className="requirestar">*</span>
                    </label>
                    <div className="btn-group">
                      <button
                        type="button"
                        className={`btn ${
                          authMethod === "email"
                            ? "btn-primary"
                            : "btn-outline-primary"
                        }`}
                        onClick={() => setAuthMethod("email")}
                      >
                        Use Email
                      </button>
                      <button
                        type="button"
                        className={`btn ${
                          authMethod === "phone_number"
                            ? "btn-primary"
                            : "btn-outline-primary"
                        }`}
                        onClick={() => setAuthMethod("phone_number")}
                      >
                        Use Phone Number
                      </button>
                    </div>
                  </div>

                  {/* Name */}
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">
                      {t("Name")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.name}
                      className="form-control"
                      placeholder={t("Enter name")}
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="name" form={formik} />
                    </span>
                  </div>

                  {/* Conditional Login Field */}
                  {authMethod === "email" ? (
                    <div className="col-md-6 form-group">
                      <label className="text-left d-flex">
                        {t("label_email")}:<span className="requirestar">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        className="form-control"
                        placeholder={t("placeholder_email")}
                      />
                      <span className="text-danger d-flex text-left">
                        <CustomError name="email" form={formik} />
                      </span>
                    </div>
                  ) : (
                    <div className="col-md-6 form-group">
                      <label className="text-left d-flex">
                        {t("label_phone_number")}:<span className="requirestar">*</span>
                      </label>
                      <PhoneInput
                        name="phone_number"
                        onChange={(e) =>
                          formik.setFieldValue("phone_number", e?.phone_number)
                        }
                        onBlur={formik.handleBlur}
                        value={formik.values.phone_number}
                        className="form-control"
                        placeholder={t("placeholder_phone_number")}
                      />
                      <span className="text-danger d-flex text-left">
                        <CustomError name="phone_number" form={formik} />
                      </span>
                    </div>
                  )}

                  {/* Gender */}
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">
                      {t("Gender")}:<span className="requirestar">*</span>
                    </label>
                    <select
                      name="gender"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.gender}
                      className="form-control"
                    >
                      <option value="">{t("Select Gender")}</option>
                      {GENDER.map((g, i) => (
                        <option key={i} value={g}>
                          {t(g)}
                        </option>
                      ))}
                    </select>
                    <span className="text-danger d-flex text-left">
                      <CustomError name="gender" form={formik} />
                    </span>
                  </div>

                  {/* Date of Birth */}
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">
                      {t("Date of Birth")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      type="date"
                      name="date_of_birth"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.date_of_birth}
                      className="form-control"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="date_of_birth" form={formik} />
                    </span>
                  </div>

                  {/* Time of Birth */}
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">
                      {t("Time of Birth")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      type="time"
                      name="time_of_birth"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.time_of_birth}
                      className="form-control"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="time_of_birth" form={formik} />
                    </span>
                  </div>

                  {/* Place of Birth with Google Autocomplete */}
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">
                      {t("Place of Birth")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      type="text"
                      name="place_of_birth"
                      ref={inputRef}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.place_of_birth}
                      className="form-control"
                      placeholder={t("Enter place of birth")}
                      autoComplete="off"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="place_of_birth" form={formik} />
                    </span>
                  </div>

                  {/* Profile Image */}
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">
                      {t("Profile Image")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      name="profile_image"
                      className="form-control"
                      onChange={(e) =>
                        formik.setFieldValue(
                          "profile_image",
                          e.target.files[0] || ""
                        )
                      }
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="profile_image" form={formik} />
                    </span>
                  </div>

                  {/* Save / Cancel Buttons */}
                  <div className="mt-5">
                    <button
                      className="btn btn-info mr-2"
                      type="submit"
                      disabled={submitted}
                    >
                      <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                      {t("btn_submit")}
                    </button>
                    <button
                      className="btn ripple btn-secondary"
                      type="button"
                      disabled={submitted}
                      onClick={() => navigate(-1)}
                    >
                      <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                      {t("btn_cancel")}
                    </button>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default UserAdd;
